import { differenceInCalendarDays, subDays } from 'date-fns';

import { formatDate } from '@common/utils/dates';

const BANNER_TAG_REGEXP = /\[\[(ad:|adtags:)([^\]]+)\]\]/;

export const determineCampaignStatus = (campaign, targeting, integrations = []) => {
  const integrationCampaigns = integrations
    .map(
      ({ defaulttext, provider, rulename }) =>
        defaulttext && {
          defaulttext: defaulttext.replace(BANNER_TAG_REGEXP, '$2'),
          provider: provider || 'Opensense',
          rulename
        }
    )
    .filter(Boolean);

  const recipientTargeting = targeting.accounts.filter((entry) => entry.name.includes('@'));
  const domainTargeting = targeting.accounts.filter((entry) => !entry.name.includes('@'));

  const domainTargetingCampaigns = domainTargeting.flatMap(({ ads, name }) => ads.map((ad) => ({ ...ad, name })) || []);
  const domainTargetingTags = domainTargeting.flatMap(({ name, tags }) => tags || []);

  const recipientTargetingCampaigns = recipientTargeting.flatMap(
    ({ ads, name }) => ads.map((ad) => ({ ...ad, name })) || []
  );
  const recipientTargetingTags = recipientTargeting.flatMap(({ name, tags }) => tags || []);

  const groupTargetingCampaigns = targeting.departments.flatMap(({ ads }) => ads || []);
  const groupTargetingTags = targeting.departments.flatMap(({ tags }) => tags || []);

  const campaignTags = campaign.tags.map(({ text }) => text);

  const signatureUsage =
    (campaign?.adusage && campaign?.adusage[campaign.rulename]?.tagblocks?.map((block) => block.rulename)) || [];

  const matchedIntegrationCampaigns =
    integrationCampaigns.filter(({ defaulttext }) => defaulttext.indexOf(campaign.rulename) > -1) ||
    campaignTags.includes(text);

  const matchedRecipientTargetingCampaigns = recipientTargetingCampaigns.filter(
    ({ text }) => text === campaign.rulename
  );
  const matchedRecipientTargetingTags = recipientTargetingTags.filter(({ text }) => campaignTags.includes(text));

  const matchedDomainTargetingCampaigns = domainTargetingCampaigns.filter(({ text }) => text === campaign.rulename);
  const matchedDomainTargetingTags = domainTargetingTags.filter(({ text }) => campaignTags.includes(text));

  const matchedGroupTargetingTags = groupTargetingTags.filter(({ text }) => campaignTags.includes(text));
  const matchedGroupTargetingCampaigns = groupTargetingCampaigns.filter(({ text }) => text === campaign.rulename);

  // console.log(
  //   'campaignTags:', campaignTags, '\n',
  //   'integrationCampaigns: ', integrationCampaigns, '\n',
  //   'targeting: ', targeting, '\n',
  //   'recipientTargeting:', recipientTargeting, '\n',
  //   'domainTargeting:', domainTargeting, '\n',
  //   'groupTargetingTags:', groupTargetingTags, '\n',
  //   'groupTargetingCampaigns:', groupTargetingCampaigns, '\n',
  //   'matchedRecipientTargetingCampaigns:', matchedRecipientTargetingCampaigns, '\n',
  //   'matchedRecipientTargetingTags:', matchedRecipientTargetingTags, '\n',
  //   'matchedDomainTargetingCampaigns:', matchedDomainTargetingCampaigns, '\n',
  //   'matchedDomainTargetingTags:', matchedDomainTargetingTags, '\n',
  //   'matchedGroupTargetingTags:', matchedGroupTargetingTags, '\n',
  //   'matchedGroupTargetingCampaigns: ', matchedGroupTargetingCampaigns, '\n',
  //   'matchedIntegrationCampaigns:', matchedIntegrationCampaigns
  // );

  const hasTargeting =
    matchedRecipientTargetingCampaigns.length ||
    matchedRecipientTargetingTags.length ||
    matchedDomainTargetingCampaigns.length ||
    matchedDomainTargetingTags.length ||
    matchedGroupTargetingTags.length ||
    matchedGroupTargetingCampaigns.length ||
    matchedIntegrationCampaigns.length ||
    signatureUsage.length ||
    campaignTags.length;

  campaign.scheduled =
    campaign?.enableschedule && campaign?.schedule?.startDate ? new Date(campaign?.schedule?.startDate) : null;
  campaign.notscheduled = !campaign?.enableschedule;
  campaign.active =
    campaign.enableschedule &&
    campaign?.schedule?.endDate &&
    new Date(campaign?.schedule?.endDate) >= new Date() &&
    !campaign.paused;

  campaign.completed = campaign?.schedule?.endDate ? new Date(campaign?.schedule?.endDate) < new Date() : false;

  if (hasTargeting) {
    campaign.hasTargeting = true;

    campaign.targetingSources = {
      signatures: {
        campaigns: signatureUsage
      },
      campaigns: {
        tags: campaignTags
      },
      integrations: {
        campaigns: matchedIntegrationCampaigns
      },
      recipient: {
        tags: [...new Set(matchedRecipientTargetingTags.map(({ text }) => text))],
        campaigns: [...new Set(matchedRecipientTargetingCampaigns.map(({ name }) => name))]
      },
      domain: {
        tags: [...new Set(matchedDomainTargetingTags.map(({ text }) => text))],
        campaigns: [...new Set(matchedDomainTargetingCampaigns.map(({ name }) => name))]
      },
      group: {
        tags: [...new Set(matchedGroupTargetingTags.map(({ text }) => text))],
        campaigns: [...new Set(matchedGroupTargetingCampaigns.map(({ text }) => text))]
      }
    };
  } else {
    delete campaign.hasTargeting;
    delete campaign.targetingSources;
  }

  return campaign;
};

export const determineLookBackPeriod = (campaign = {}, defaultStarteDate) => {
  const today = new Date();
  const startDate = new Date();
  const fallbackStartDate = defaultStarteDate
    ? new Date(defaultStarteDate)
    : startDate.setDate(startDate.getDate() - 365);
  const start =
    campaign?.enableschedule && campaign?.schedule?.startDate
      ? new Date(campaign?.schedule?.startDate)
      : fallbackStartDate;
  const daysLookback = Math.max(1, Math.ceil((today - start) / (1000 * 60 * 60 * 24)));

  return daysLookback;
};

/**
 * Metrics are fetched one getadreport call per campaign. On a domain with hundreds of
 * campaigns, firing them all at once queues past the browser's per-origin cap and lands
 * every response together for one long parse. Six lanes keeps that bounded.
 */
export const METRICS_REQUEST_CONCURRENCY = 6;

/**
 * Resolves the `{ start, days }` pair for a getadreport request.
 *
 * getadreport only speaks "last N days", so `days` always counts back from today — but it
 * must only reach as far as the window actually being rendered. determineLookBackPeriod
 * ignores the start it is handed whenever the campaign carries its own schedule.startDate,
 * so a long-running campaign pulled its entire lifetime of daily metrics just to draw a
 * 60-day view, and getMetrics then discarded everything outside the window client-side.
 *
 * Callers that genuinely want the whole campaign lifetime — the campaign edit view, and the
 * all-campaigns CSV export — opt into it with `fullHistory`.
 */
export const resolveMetricsWindow = ({
  campaign = {},
  fullHistory = false,
  customStart = null,
  customEnd = null,
  daysOverride = null,
  startDate,
  now = new Date()
} = {}) => {
  const isCustomRange = !!(customStart && customEnd);

  const start = isCustomRange
    ? new Date(customStart)
    : daysOverride
      ? subDays(now, daysOverride - 1)
      : fullHistory && campaign?.enableschedule && campaign?.schedule?.startDate
        ? new Date(campaign.schedule.startDate)
        : new Date(startDate);

  const days = Math.max(1, differenceInCalendarDays(now, start) + 1);

  return { start, days };
};

// ─── Campaign report export helpers (see plans/20260630-campaign-report-export-columns.md) ───

/**
 * Returns the i18n *key* for a campaign's 6-state status label, mirroring the on-screen
 * decision tree in cell-formatters/status.svelte (active-first ordering). Returns the key
 * (not the translated string) so this stays free of the svelte-i18n store and unit-testable;
 * callers do `$format(getCampaignStatusLabel(record))`. `now` is injectable for deterministic
 * tests. Un-decorated rows (none of the status flags, no activeStatus) return '' rather than
 * mislabeling as INACTIVE.
 */
export const getCampaignStatusLabel = (record, now = Date.now()) => {
  if (!record) {
    return '';
  }

  const { active, paused, notscheduled, completed, scheduled, targetingSources, activeStatus } = record;

  if (!active && !paused && !notscheduled && !completed && !activeStatus) {
    return '';
  }

  if (active && new Date(record?.schedule?.startDate).getTime() >= now) {
    return 'label.CAMPAIGN_UPCOMING';
  }
  if (active) {
    return 'label.ACTIVE';
  }
  if (paused) {
    return 'label.PAUSED';
  }
  if (notscheduled && targetingSources) {
    return 'label.ACTIVE';
  }
  if (completed && scheduled) {
    return 'label.CAMPAIGN_EXPIRED';
  }

  return 'label.INACTIVE';
};

/**
 * Click-through destination for the export. Only `linktype === 'url'` has a real, stored
 * destination on the client object (the landingpage override is already baked into `url` at
 * save time); doc/video export a blank cell per the 2026-06-30 product decision.
 */
export const buildDestinationCell = (record) => (record?.linktype === 'url' ? record.url || '' : '');

// The four appended export header keys, in order. Shared by both export sites and the tests so
// header order cannot drift from the row cells built by buildExportExtensionCells.
export const NEW_EXPORT_HEADER_KEYS = ['label.START_DATE', 'label.END_DATE', 'label.STATUS', 'label.DESTINATION_URL'];

/**
 * Builds the four appended export cells (in NEW_EXPORT_HEADER_KEYS order) from a campaign object.
 * `formatLabel` is injected (each .svelte caller passes its `$format`) to keep this pure and
 * testable with a stub formatter. Used identically by both onExportCampaigns sites so the new
 * columns cannot diverge between them.
 */
export const buildExportExtensionCells = (campaign, formatLabel) => [
  campaign?.schedule?.startDate ? formatDate(campaign.schedule.startDate, 'MM/dd/yyyy') : '',
  campaign?.schedule?.endDate ? formatDate(campaign.schedule.endDate, 'MM/dd/yyyy') : '',
  formatLabel(getCampaignStatusLabel(campaign)),
  buildDestinationCell(campaign)
];
